import React, { useCallback, useEffect, useState } from "react";
import { v4 as uuid } from "uuid";

import { useWorkorderCreation } from "../../data/Workorders/useWorkorderCreation";
import { Asset } from "../../shared/interfaces/Asset";
import {
  NewWorkorderWithMetadata,
  WorkorderDetails,
  WorkorderLocationAndWorkflow,
} from "../../shared/interfaces/WorkOrder";
import { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";
import { WorkflowV2 } from "../../shared/interfaces/WorkflowV2";
import { RecurringTargetDates, useRecurringTargetDates } from "./useRecurringTargetDates";

type BulkCreationStatus = "pending" | "loading" | "allError" | "complete";

export interface IBulkWorkorderContext extends RecurringTargetDates {
  workorderDetails: Partial<WorkorderDetails>;
  updateWorkorderDetailsField: (key: keyof WorkorderDetails, value: unknown) => void;
  updateTargetStartDate: (value: Date | undefined) => void;
  updateTargetCompletionDate: (value: Date | undefined) => void;
  locationsAndWorkflows: WorkorderLocationAndWorkflowGroup[];
  addLocationAndWorkflowGroup: () => void;
  editGroupSubstation: (groupId: string, substationId: number | undefined, substationName: string | undefined) => void;
  editGroupSwitchgear: (groupId: string, assets: GroupSwitchgear[] | undefined) => void;
  editGroupWorkflow: (groupId: string, workflow: WorkflowV1 | WorkflowV2 | undefined) => void;
  editGroupCompanyNames: (groupId: string, companyName: string | undefined, companyId: number | undefined) => void;
  editGroupSiteNames: (groupId: string, siteName: string | undefined, siteId: number | undefined) => void;
  removeLocationAndWorkflowGroup: (groupId: string) => void;
  workorderList: NewWorkorderWithMetadata[];
  createWorkorders: () => void;
  bulkCreationStatus: BulkCreationStatus;
  assetsByGroupId: Record<string, Asset[]>;
  editGroupAssets: (groupId: string, assets: Asset[]) => void;
}

export interface GroupSwitchgear {
  assetId: number;
  assetName: string;
}

export interface WorkorderLocationAndWorkflowGroup {
  groupId: string;
  companyName?: string;
  siteName?: string;
  companyId?: number;
  siteId?: number;
  assetNames?: string[];
  workflowName?: string;
  locationAndWorkflow: Partial<WorkorderLocationAndWorkflow>;
}

export const BulkWorkorderContext = React.createContext({} as IBulkWorkorderContext);

export function BulkWorkorderContextProvider({ children }: React.PropsWithChildren<Record<never, never>>) {
  const workorderCreation = useWorkorderCreation<NewWorkorderWithMetadata>({
    onSuccess: onWorkorderCreationSuccess,
    onError: onWorkorderCreationError,
  });
  const recurringDates = useRecurringTargetDates();

  const [workorderDetails, setWorkorderDetails] = useState<Partial<WorkorderDetails>>({});
  const [locationsAndWorkflows, setLocationsAndWorkflows] = useState<WorkorderLocationAndWorkflowGroup[]>([
    { groupId: uuid(), locationAndWorkflow: {} },
  ]);
  const [workorderList, setWorkorderList] = useState<NewWorkorderWithMetadata[]>([]);
  const [bulkCreationStatus, setBulkCreationStatus] = useState<BulkCreationStatus>("pending");
  const [assetsByGroupId, setAssetsByGroupId] = useState<Record<string, Asset[]>>({});

  useEffect(() => {
    if (workorderList.every((w) => w.creationStatus === "pending")) setBulkCreationStatus("pending");
    else if (workorderList.some((w) => w.creationStatus === "loading")) setBulkCreationStatus("loading");
    else if (workorderList.every((w) => w.creationStatus === "error")) setBulkCreationStatus("allError");
    else if (workorderList.every((w) => w.creationStatus === "success" || w.creationStatus === "error"))
      setBulkCreationStatus("complete");
  }, [workorderList]);

  useEffect(() => {
    const updatedWorkorderList = locationsAndWorkflows.flatMap((locationAndWorkflow) => {
      if (recurringDates.targetDates.length > 0) {
        return recurringDates.targetDates.map((targetDates) => {
          return {
            uuid: uuid(),
            assetNames: locationAndWorkflow.assetNames,
            workflowName: locationAndWorkflow.workflowName,
            creationStatus: "pending",
            newWorkorder: {
              ...workorderDetails,
              targetStartDate: targetDates.targetStartDate,
              targetCompletionDate: targetDates.targetCompletionDate,
              ...locationAndWorkflow.locationAndWorkflow,
            },
          } as NewWorkorderWithMetadata;
        });
      } else {
        return {
          uuid: locationAndWorkflow.groupId,
          assetNames: locationAndWorkflow.assetNames,
          workflowName: locationAndWorkflow.workflowName,
          creationStatus: "pending",
          newWorkorder: {
            ...workorderDetails,
            ...locationAndWorkflow.locationAndWorkflow,
          },
        } as NewWorkorderWithMetadata;
      }
    });

    setWorkorderList(updatedWorkorderList);
  }, [workorderDetails, locationsAndWorkflows, recurringDates.targetDates]);

  function onWorkorderCreationSuccess(id: number, newWorkorderWithMetadata: Partial<NewWorkorderWithMetadata>) {
    setWorkorderList((prevState) => {
      const foundGroup = prevState.find((group) => group.uuid === newWorkorderWithMetadata.uuid);

      if (foundGroup) {
        foundGroup.createdWorkorderId = id;
        foundGroup.creationStatus = "success";
      }
      return [...prevState];
    });
  }

  function onWorkorderCreationError(error: Response, variables: NewWorkorderWithMetadata) {
    setWorkorderList((prevState) => {
      const foundGroup = prevState.find((group) => group.uuid === variables.uuid);

      if (foundGroup) {
        foundGroup.creationStatus = "error";
      }
      return [...prevState];
    });
  }

  function createWorkorders() {
    setWorkorderList((prevState) => {
      return prevState.map((w) => ({ ...w, creationStatus: "loading" }));
    });

    workorderList.forEach((workorder) => workorderCreation.mutate(workorder));
  }

  const updateWorkorderDetailsField = useCallback((key: keyof WorkorderDetails, value: unknown) => {
    setWorkorderDetails((prevState) => ({ ...prevState, [key]: value }));
  }, []);

  function updateTargetStartDate(targetStartDate: Date | undefined) {
    let targetCompletionDate = workorderDetails.targetCompletionDate;

    //if no target completion has been set, set it to be the same as the target start
    if (!workorderDetails.targetCompletionDate && targetStartDate) targetCompletionDate = targetStartDate;

    recurringDates.setTargetStartDate(targetStartDate);
    recurringDates.setTargetCompletionDate(targetCompletionDate);
    setWorkorderDetails((prevState) => ({
      ...prevState,
      targetStartDate,
      targetCompletionDate,
    }));
  }

  function updateTargetCompletionDate(targetCompletionDate: Date | undefined) {
    let targetStartDate = workorderDetails.targetStartDate;

    //if no target start has been set, set it to be the same as the target completion
    if (!workorderDetails.targetStartDate && targetCompletionDate) targetStartDate = targetCompletionDate;

    recurringDates.setTargetStartDate(targetStartDate);
    recurringDates.setTargetCompletionDate(targetCompletionDate);
    setWorkorderDetails((prevState) => ({
      ...prevState,
      targetStartDate,
      targetCompletionDate,
    }));
  }

  function addLocationAndWorkflowGroup() {
    setLocationsAndWorkflows((prevState) => [
      ...prevState,
      {
        groupId: uuid(),
        locationAndWorkflow: {},
        companyName: "",
        siteName: "",
        companyId: undefined,
        siteId: undefined,
      },
    ]);
  }

  function editGroupSubstation(groupId: string, substationId: number | undefined, substationName: string | undefined) {
    setLocationsAndWorkflows((prevState) => {
      const foundGroup = prevState.find((group) => group.groupId === groupId);

      if (foundGroup && foundGroup.locationAndWorkflow) {
        foundGroup.locationAndWorkflow.substationId = substationId;
        foundGroup.locationAndWorkflow.substationName = substationName;
      }
      return [...prevState];
    });
  }

  function editGroupSwitchgear(groupId: string, assets: GroupSwitchgear[] | undefined) {
    setLocationsAndWorkflows((prevState) => {
      const foundGroup = prevState.find((group) => group.groupId === groupId);

      if (foundGroup && foundGroup.locationAndWorkflow) {
        foundGroup.assetNames = assets?.map((asset) => asset.assetName);
        foundGroup.locationAndWorkflow.assetIds = assets?.map((asset) => asset.assetId);
      }

      return [...prevState];
    });
  }

  function editGroupWorkflow(groupId: string, workflow: WorkflowV1 | WorkflowV2 | undefined) {
    setLocationsAndWorkflows((prevState) => {
      const foundGroup = prevState.find((group) => group.groupId === groupId);

      if (foundGroup && foundGroup.locationAndWorkflow) {
        foundGroup.workflowName = workflow?.description;
        foundGroup.locationAndWorkflow.workflowId = workflow?.id;
        foundGroup.locationAndWorkflow.workflowDetail = workflow?.workflowDetail;
        foundGroup.locationAndWorkflow.workflowRevisionNo = workflow?.revisionNo;
      }

      return [...prevState];
    });
  }

  function editGroupCompanyNames(groupId: string, companyName: string | undefined, companyId: number | undefined) {
    setLocationsAndWorkflows((prevState) => {
      const foundGroup = prevState.find((group) => group.groupId === groupId);
      if (foundGroup) {
        foundGroup.companyName = companyName;
        foundGroup.companyId = companyId;
      }
      return [...prevState];
    });
  }

  function editGroupSiteNames(groupId: string, siteName: string | undefined, siteId: number | undefined) {
    setLocationsAndWorkflows((prevState) => {
      const foundGroup = prevState.find((group) => group.groupId === groupId);
      if (foundGroup) {
        foundGroup.siteName = siteName;
        foundGroup.siteId = siteId;
      }
      return [...prevState];
    });
  }

  function removeLocationAndWorkflowGroup(groupId: string) {
    setLocationsAndWorkflows((prevState) => prevState.filter((group) => group.groupId !== groupId));
  }

  const editGroupAssets = useCallback((groupId: string, assets: Asset[]) => {
    setAssetsByGroupId((prev) => ({
      ...prev,
      [groupId]: assets,
    }));
  }, []);

  return (
    <BulkWorkorderContext.Provider
      value={{
        workorderDetails,
        updateWorkorderDetailsField,
        updateTargetStartDate,
        updateTargetCompletionDate,
        locationsAndWorkflows,
        addLocationAndWorkflowGroup,
        editGroupSubstation,
        editGroupSwitchgear,
        editGroupWorkflow,
        editGroupCompanyNames,
        editGroupSiteNames,
        removeLocationAndWorkflowGroup,
        workorderList,
        createWorkorders,
        bulkCreationStatus,
        assetsByGroupId,
        editGroupAssets,
        ...recurringDates,
      }}
    >
      {children}
    </BulkWorkorderContext.Provider>
  );
}
